import { mergeAttributes, Node } from '@tiptap/core';
import { optimizeStyle } from './utilityFunctions';
import { Plugin, PluginKey } from 'prosemirror-state';
import { Attrs } from 'prosemirror-model';

export interface ParagraphOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customParagraph: {
      /**
       * Toggle a paragraph
       */
      setCustomParagraph: (attributes: {
        style?: string | null;
        attrs?: Attrs | null;
      }) => ReturnType;
    };
  }
}

export const CustomizedParagraph = Node.create<ParagraphOptions>({
  name: 'paragraph',

  priority: 1006,

  addOptions() {
    return {
      HTMLAttributes: {},
    };
  },

  group: 'block',
  content: 'inline*',
  isolating: false,
  draggable: false,

  parseHTML() {
    return [{ tag: 'p' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['p', optimizeStyle(mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)), 0];
  },
  addCommands() {
    return {
      setCustomParagraph:
        (attributes) =>
        ({ commands }) => {
          const mergedAttrs = { ...attributes.attrs, style: attributes.style };
          return commands.setNode(this.name, mergedAttrs);
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      'Mod-Alt-0': () => this.editor.commands.setCustomParagraph({}),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('paragraphEmptySpanPlugin'),

        appendTransaction: (transactions, oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) return null;

          const { doc, tr } = newState;
          let modified = false;
          doc.descendants((node, pos) => {
            if (node.type.name === 'paragraph') {
              const hasContent = node.textContent.trim().length > 0;

              if (hasContent) {
                node.descendants((child, childPos) => {
                  if (
                    child.isText &&
                    child.marks.some(
                      (m) =>
                        m.type.name === 'textStyle' &&
                        m.attrs?.class === 'lint-editor-is-empty-class',
                    )
                  ) {
                    tr.delete(pos + childPos, pos + childPos + child.nodeSize);
                  }
                });

                node.descendants((child, childPos) => {
                  if (
                    child.type.name === 'hardBreak' &&
                    child.attrs?.class === 'ProseMirror-trailingBreak'
                  ) {
                    tr.delete(pos + childPos, pos + childPos + child.nodeSize);
                  }
                });
              }

              if (!hasContent) {
                const currentNode = tr.doc.nodeAt(pos);
                if (currentNode && currentNode.type.name === 'paragraph') {
                  const newAttrs = { ...currentNode.attrs, class: 'lint-editor-is-empty-class' };
                  tr.setNodeMarkup(pos, currentNode.type, newAttrs);
                }

                const emptySpanMark = newState.schema.marks.textStyle.create({
                  class: 'lint-editor-is-empty-class',
                });
                const brNode = newState.schema.nodes.hardBreak.create();

                const emptySpanText = newState.schema.text(' ', [emptySpanMark]);
                tr.insert(pos + 1, emptySpanText);
                tr.insert(pos + 2, brNode);
              }
            }
          });
          return modified ? tr : null;
        },
      }),
    ];
  },
});

export const EmptySpan = Node.create({
  name: 'emptySpan',
  inline: true,
  group: 'inline',
  content: 'hardBreak',
  selectable: false,
  atom: true,
  parseHTML: () => [{ tag: 'span.lint-editor-is-empty-class' }],
  renderHTML: () => ['span', { class: 'lint-editor-is-empty-class' }, 0],
});
